import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Phone, ArrowRight } from 'lucide-react';
import { CompanyConfig } from '@/constants/cx-companies';

interface CXLandingProps {
  config: CompanyConfig;
  onStartCall: () => void;
}

export const CXLanding: React.FC<CXLandingProps> = ({
  config,
  onStartCall,
}) => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src={config.logo} alt={config.name} className="h-8" />
            </div>
          </div>
        </div>
      </div>

      {/* Brown banner */}
      <div
        style={{ backgroundColor: config.colors.primaryDark }}
        className="h-12 w-full"
      />

      {/* Main content */}
      <div className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start max-w-7xl mx-auto">
          {/* Left side - Hero */}
          <div>
            <h1
              className="text-3xl md:text-5xl font-bold mb-6 md:mb-8"
              style={{ color: config.colors.primaryDark }}
            >
              {config.landing.headline}
            </h1>

            <p className="text-gray-600 mb-2">
              Can't find your question? View all our{' '}
              <a href="https://www.chocolatefinance.com/faqs" target='_blank' className="text-pink-500 font-medium">
                FAQ's
              </a>
              ,
            </p>
            <p className="text-gray-600 mb-2">
              chat with us on{' '}
              <a href="#" className="text-pink-500 font-medium">
                WhatsApp
              </a>
              , or email us via
            </p>
            <p className="text-pink-500 font-medium mb-8">
              hello@chocolatefinance.com
            </p>

            {/* Talk to Rich card */}
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-2xl p-4 md:p-6 border border-pink-200">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
                    {config.landing.subheadline}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600">
                    Your AI financial advisor for all things money: savings
                    plans, investments strategies, and more. I'm here 24/7,
                    minus the hold music.
                  </p>
                </div>
                <button
                  onClick={onStartCall}
                  className="p-2.5 md:p-3 bg-pink-500 rounded-full hover:bg-pink-600 transition-all hover:scale-105 flex-shrink-0 shadow-md self-end sm:self-auto"
                >
                  <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Right side - FAQs */}
          <div className="bg-white rounded-xl p-4 md:p-6 shadow-sm mt-8 lg:mt-0">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {config.landing.faqs.map((faq, index) => (
                <div key={index} className="border-b border-gray-100 pb-3 last:border-b-0">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between text-left hover:text-gray-700 transition-colors group"
                  >
                    <span className="text-sm font-medium text-gray-700 pr-3">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 group-hover:text-gray-600" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="mt-2 text-gray-600 text-xs leading-relaxed pl-1">
                      {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};
